import { ApolloError } from '@apollo/client';
import { useCallback, useMemo } from 'react';

import { useDeleteHypotheticalInsuranceMutation } from '@/modules/entities/details/HypotheticalILITModal/graphql/HypotheticalILITModalData.generated';
import { useDeleteEstateWaterfallHypotheticalTransfersMutation } from '@/modules/entities/details/HypotheticalTransfersCard/hypotheticalTransfers/graphql/DeleteEstateWaterfallHypotheticalTransfers.generated';
import {
  useDeleteEstateWaterfallHypotheticalCltMutation,
  useDeleteEstateWaterfallHypotheticalCrtMutation,
} from '@/modules/estateWaterfall/components/EstateWaterfallHypotheticalCharitableTrustModal/graphql/EstateWaterfallHypotheticalCharitableTrustModal.generated';
import { useDeleteEstateWaterfallHypotheticalGratsMutation } from '@/modules/estateWaterfall/components/EstateWaterfallHypotheticalGratModal/graphql/EstateWaterfallHypotheticalGratModal.generated';
import { useDeleteEstateWaterfallHypotheticalSaleLoanMutation } from '@/modules/estateWaterfall/components/EstateWaterfallHypotheticalSaleLoanModal/graphql/EstateWaterfallHypotheticalSaleLoanModal.generated';
import { Operations as EstateWaterfallOperations } from '@/modules/estateWaterfall/graphql/EstateWaterfall.generated';
import { Operations as GetWaterfallSummaryOperations } from '@/modules/estateWaterfall/graphql/GetWaterfallSummary.generated';
import { Operations as ShowWaterfallSummaryTileDetailsOperations } from '@/modules/estateWaterfall/graphql/ShowWaterfallSummaryTileDetails.generated';
import { Operations as HypotheticalTransfersSummaryOperations } from '@/modules/summaryPanels/HypotheticalTransfersSummaryPanel/graphql/HypotheticalTransfersSummary.generated';
import { diagnostics } from '@/utils/diagnostics';
import { getPulidKind, PulidKind } from '@/utils/pulid';

export const HYPOTHETICAL_SIDEBAR_QUERIES_TO_REFETCH = [
  HypotheticalTransfersSummaryOperations.Query
    .HypotheticalTransfersSummaryQuery,
  EstateWaterfallOperations.Query.EstateWaterfall,
  GetWaterfallSummaryOperations.Query.GetWaterfallSummary,
  ShowWaterfallSummaryTileDetailsOperations.Query
    .ShowWaterfallSummaryTileDetails,
];

type DeleteHypotheticalCallback = (transferId: string) => Promise<void>;

type DeleteHypotheticalStatus = {
  loading: boolean;
  error: ApolloError | undefined;
};

export type UseDeleteHypotheticalOutput = [
  DeleteHypotheticalCallback,
  DeleteHypotheticalStatus,
];

export function useDeleteHypothetical(): UseDeleteHypotheticalOutput {
  const [
    deleteEstateWaterfallHypotheticalTransfers,
    {
      loading: deletingEstateWaterfallHypotheticalTransfers,
      error: deletingEstateWaterfallHypotheticalTransfersError,
    },
  ] = useDeleteEstateWaterfallHypotheticalTransfersMutation({
    refetchQueries: HYPOTHETICAL_SIDEBAR_QUERIES_TO_REFETCH,
  });
  const [
    deleteHypotheticalSaleLoan,
    {
      loading: deletingHypotheticalSaleLoan,
      error: deletingHypotheticalSaleLoanError,
    },
  ] = useDeleteEstateWaterfallHypotheticalSaleLoanMutation({
    refetchQueries: HYPOTHETICAL_SIDEBAR_QUERIES_TO_REFETCH,
  });
  const [
    deleteHypotheticalCrt,
    { loading: deletingHypotheticalCrt, error: deletingHypotheticalCrtError },
  ] = useDeleteEstateWaterfallHypotheticalCrtMutation({
    refetchQueries: HYPOTHETICAL_SIDEBAR_QUERIES_TO_REFETCH,
  });
  const [
    deleteHypotheticalClt,
    { loading: deletingHypotheticalClt, error: deletingHypotheticalCltError },
  ] = useDeleteEstateWaterfallHypotheticalCltMutation({
    refetchQueries: HYPOTHETICAL_SIDEBAR_QUERIES_TO_REFETCH,
  });
  const [
    deleteHypotheticalGrats,
    { loading: deletingHypotheticalGrat, error: deletingHypotheticalGratError },
  ] = useDeleteEstateWaterfallHypotheticalGratsMutation({
    refetchQueries: HYPOTHETICAL_SIDEBAR_QUERIES_TO_REFETCH,
  });
  const [
    deleteHypotheticalInsurance,
    {
      loading: deletingHypotheticalInsurance,
      error: deletingHypotheticalInsuranceError,
    },
  ] = useDeleteHypotheticalInsuranceMutation({
    refetchQueries: HYPOTHETICAL_SIDEBAR_QUERIES_TO_REFETCH,
  });

  const loading = useMemo(
    () =>
      deletingEstateWaterfallHypotheticalTransfers ||
      deletingHypotheticalSaleLoan ||
      deletingHypotheticalCrt ||
      deletingHypotheticalClt ||
      deletingHypotheticalGrat ||
      deletingHypotheticalInsurance,
    [
      deletingEstateWaterfallHypotheticalTransfers,
      deletingHypotheticalClt,
      deletingHypotheticalCrt,
      deletingHypotheticalGrat,
      deletingHypotheticalSaleLoan,
      deletingHypotheticalInsurance,
    ]
  );

  const error = useMemo(
    () =>
      deletingHypotheticalSaleLoanError ||
      deletingHypotheticalCrtError ||
      deletingHypotheticalCltError ||
      deletingHypotheticalGratError ||
      deletingEstateWaterfallHypotheticalTransfersError ||
      deletingHypotheticalInsuranceError,
    [
      deletingHypotheticalSaleLoanError,
      deletingHypotheticalCrtError,
      deletingHypotheticalCltError,
      deletingHypotheticalGratError,
      deletingEstateWaterfallHypotheticalTransfersError,
      deletingHypotheticalInsuranceError,
    ]
  );

  const callback: DeleteHypotheticalCallback = useCallback(
    async (transferId: string) => {
      const pulid = getPulidKind(transferId);
      switch (pulid) {
        case PulidKind.HypotheticalSaleLoan:
          await deleteHypotheticalSaleLoan({
            variables: { hslId: transferId },
          });
          break;
        case PulidKind.HypotheticalGrat:
          await deleteHypotheticalGrats({
            variables: { hgratIds: [transferId] },
          });
          break;
        case PulidKind.HypotheticalCrt:
          await deleteHypotheticalCrt({
            variables: { hcrtIds: [transferId] },
          });
          break;
        case PulidKind.HypotheticalClt:
          await deleteHypotheticalClt({
            variables: { hcltIds: [transferId] },
          });
          break;
        case PulidKind.EstateWaterfallHypotheticalTransfer:
          await deleteEstateWaterfallHypotheticalTransfers({
            variables: {
              estateWaterfallHypotheticalTransferIds: [transferId],
            },
          });
          break;
        case PulidKind.HypotheticalInsurance:
          await deleteHypotheticalInsurance({
            variables: { hilitIds: [transferId] },
          });
          break;
        default: {
          diagnostics.warn(
            `Attempted to delete non-hypothetical pulid: ${pulid} for transferId: ${transferId}`
          );
          return;
        }
      }
    },
    [
      deleteEstateWaterfallHypotheticalTransfers,
      deleteHypotheticalClt,
      deleteHypotheticalCrt,
      deleteHypotheticalGrats,
      deleteHypotheticalInsurance,
      deleteHypotheticalSaleLoan,
    ]
  );

  return [callback, { loading, error }];
}
